import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr, fmtDate } from "../api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import StatusBadge from "../components/StatusBadge";

const ROLES = ["admin", "production", "inventory", "packing", "staff"];

function UserDialog({ open, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "inventory" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ name: "", email: "", password: "", role: "inventory" }); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/register", f);
      toast.success("User created");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="user-dialog">
        <DialogHeader><DialogTitle className="font-heading text-[#002B5D]">Add User</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="sk-label">Name *</label>
            <input data-testid="user-name-input" required className="sk-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          </div>
          <div>
            <label className="sk-label">Email *</label>
            <input data-testid="user-email-input" type="email" required className="sk-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          </div>
          <div>
            <label className="sk-label">Password *</label>
            <input data-testid="user-password-input" type="text" required minLength={6} className="sk-input font-mono" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
          </div>
          <div>
            <label className="sk-label">Role</label>
            <select data-testid="user-role-select" className="sk-input" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
              {ROLES.map((r) => <option key={r} value={r} className="capitalize">{r === "staff" ? "staff (production + inventory + packing)" : r}</option>)}
            </select>
          </div>
          <button data-testid="user-save-button" disabled={busy} className="w-full h-11 rounded-md font-heading font-bold text-white disabled:opacity-60" style={{ backgroundColor: "#002B5D" }}>
            {busy ? "Creating..." : "Create User"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TareSettings() {
  const [tare, setTare] = useState("");
  const [lotPrefix, setLotPrefix] = useState("SKZLOT");
  const [lotRunning, setLotRunning] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/settings").then((r) => {
      setTare(String(r.data.tare_weight));
      setLotPrefix(r.data.lot_prefix || "SKZLOT");
      setLotRunning(String(r.data.lot_running ?? 0));
    }).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch("/settings", { tare_weight: parseFloat(tare), lot_prefix: lotPrefix, lot_running: parseInt(lotRunning) });
      toast.success("Settings updated");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sk-card p-5 max-w-2xl" data-testid="settings-card">
      <h3 className="font-heading font-bold text-[#002B5D]">System Settings</h3>
      <form onSubmit={save} className="mt-3 grid sm:grid-cols-3 gap-4 items-end">
        <div>
          <label className="sk-label">Tare Weight (KG)</label>
          <input data-testid="tare-weight-input" type="number" step="any" min="0" required className="sk-input font-mono" value={tare} onChange={(e) => setTare(e.target.value)} />
          <p className="text-[10px] text-slate-400 mt-1">Added to Net for Gross Weight on Packing Lists</p>
        </div>
        <div>
          <label className="sk-label">Lot Prefix</label>
          <input data-testid="lot-prefix-input" className="sk-input font-mono" value={lotPrefix} onChange={(e) => setLotPrefix(e.target.value)} />
        </div>
        <div>
          <label className="sk-label">Running number: {lotRunning || 0}</label>
          <input data-testid="lot-running-input" type="number" min="0" required className="sk-input font-mono" value={lotRunning} onChange={(e) => setLotRunning(e.target.value)} />
          <p className="text-[10px] text-slate-400 mt-1" data-testid="lot-next-preview">Next lot: {lotPrefix || "SKZLOT"}-{(parseInt(lotRunning) || 0) + 1}</p>
        </div>
        <div className="sm:col-span-3">
          <button data-testid="settings-save" disabled={busy} className="h-10 px-6 rounded-md font-heading font-bold text-sm text-white disabled:opacity-60" style={{ backgroundColor: "#002B5D" }}>
            {busy ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Users() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => {
    if (user && user.role !== "admin") { navigate("/"); return; }
    load();
  }, [user, navigate]);

  const patch = async (id, body) => {
    try {
      await api.patch(`/users/${id}`, body);
      toast.success("Updated");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="users-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-extrabold text-[#002B5D] tracking-tight">Users & Roles</h1>
        <button data-testid="add-user-button" onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-md font-heading font-bold text-sm text-white" style={{ backgroundColor: "#002B5D" }}>
          <Plus className="w-4 h-4" /> Add User
        </button>
      </div>
      <TareSettings />
      <div className="sk-card overflow-x-auto">
        <table className="w-full sk-table" data-testid="users-table">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Created</th><th></th></tr></thead>
          <tbody>
            {!users && <tr><td colSpan={6} className="text-center text-slate-400">Loading...</td></tr>}
            {users?.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.email}`}>
                <td className="font-medium">{u.name}</td>
                <td className="font-mono text-xs">{u.email}</td>
                <td>
                  <select data-testid={`user-role-${u.email}`} className="sk-input h-8 w-32 text-xs" value={u.role}
                    onChange={(e) => patch(u.id, { role: e.target.value })} disabled={u.email === user?.email}>
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td>
                  <span data-testid={`user-status-${u.email}`} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${u.active === false ? "bg-slate-100 text-slate-500 border-slate-300" : "bg-green-100 text-green-700 border-green-300"}`}>
                    {u.active === false ? "Disabled" : "Active"}
                  </span>
                </td>
                <td className="text-slate-500 text-xs">{fmtDate(u.created_at)}</td>
                <td>
                  {u.email !== user?.email && (
                    <span className="inline-flex gap-1">
                      <button data-testid={`user-customlot-${u.email}`} onClick={() => patch(u.id, { can_custom_lot: !u.can_custom_lot })}
                        title="Allow this user to set custom lot numbers when generating a lot"
                        className={`px-2 py-1 rounded text-xs font-semibold ${u.can_custom_lot ? "text-[#002B5D] bg-sky-50 hover:bg-sky-100" : "text-slate-400 hover:bg-slate-100"}`}>
                        {u.can_custom_lot ? "Custom Lot: ON" : "Custom Lot: OFF"}
                      </button>
                      <button data-testid={`user-toggle-${u.email}`} onClick={() => patch(u.id, { active: u.active === false })}
                        className={`px-2 py-1 rounded text-xs font-semibold ${u.active === false ? "text-green-700 hover:bg-green-50" : "text-rose-600 hover:bg-rose-50"}`}>
                        {u.active === false ? "Enable" : "Disable"}
                      </button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <UserDialog open={open} onClose={() => setOpen(false)} onSaved={load} />
    </div>
  );
}
